import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Paper,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  AutoFixHigh,
  Rocket,
  Security,
  Speed,
  Psychology,
  Code,
  Cloud,
  Analytics,
  ArrowForward,
  CheckCircle,
  BrainIcon,
  SmartToy,
  Verified,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  // Enhanced color palette
  const colors = {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    accent: '#06b6d4',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    dark: '#1e293b',
    light: '#f8fafc',
    gradient1: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  };

  // Core values and features
  const values = [
    {
      icon: <Psychology sx={{ fontSize: 40 }} />,
      title: 'AI-Powered Optimization',
      description: 'Advanced algorithms analyze your experience and optimize content structure, keyword placement, and formatting for maximum impact with both ATS systems and human recruiters.',
      color: colors.primary,
      gradient: colors.gradient1,
    },
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: 'Fast & Efficient',
      description: 'Generate a professional, well-structured CV in minutes. Our streamlined process guides you through each section with intelligent suggestions and formatting.',
      color: colors.accent,
      gradient: colors.gradient3,
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: 'Privacy Focused',
      description: 'Your personal information is protected with industry-standard security measures. We prioritize your privacy and never share your data with third parties.',
      color: colors.secondary,
      gradient: colors.gradient2,
    },
    {
      icon: <Verified sx={{ fontSize: 40 }} />,
      title: 'Modern Standards',
      description: 'Stay current with the latest CV trends and recruitment practices. Our templates and suggestions are designed for today\'s competitive job market.',
      color: colors.success,
      gradient: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
    },
  ];

  // Technology features
  const techStack = [
    { name: 'Natural Language Processing', icon: <Code />, color: colors.primary },
    { name: 'Machine Learning Models', icon: <Psychology />, color: colors.secondary },
    { name: 'Cloud Infrastructure', icon: <Cloud />, color: colors.accent },
    { name: 'Advanced Analytics', icon: <Analytics />, color: colors.success },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  const floatingVariants = {
    animate: {
      y: [0, -10, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* Epic Hero Section */}
      <Box
        sx={{
          position: 'relative',
          py: 20,
          background: `linear-gradient(135deg, ${colors.primary}ee, ${colors.secondary}ee, ${colors.accent}ee)`,
          color: '#fff',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Animated background elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            '&::before': {
              content: '""',
              position: 'absolute',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 2px, transparent 2px)',
              backgroundSize: '50px 50px',
              animation: 'float 20s linear infinite',
            },
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 900,
                mb: 4,
                fontSize: { xs: '3rem', md: '4.5rem', lg: '6rem' },
                textShadow: '4px 4px 8px rgba(0,0,0,0.3)',
                background: 'linear-gradient(45deg, #fff 30%, #f0f9ff 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              cleverCV
            </Typography>
            <Typography
              variant="h3"
              sx={{
                mb: 6,
                maxWidth: '900px',
                mx: 'auto',
                fontWeight: 300,
                lineHeight: 1.3,
              }}
            >
              Smart CV Creation for the Modern Job Market
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 8,
                maxWidth: '700px',
                mx: 'auto',
                opacity: 0.9,
                lineHeight: 1.6,
              }}
            >
              Harness the power of artificial intelligence to create compelling, 
              ATS-optimized CVs that get you noticed by recruiters and hiring managers.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/generate"
                  sx={{
                    px: 6,
                    py: 3,
                    borderRadius: '50px',
                    backgroundColor: '#fff',
                    color: colors.primary,
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    '&:hover': {
                      backgroundColor: '#f8fafc',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
                    },
                  }}
                  startIcon={<Rocket />}
                >
                  Start Creating
                </Button>
              </motion.div>
            </Box>
          </motion.div>
        </Container>
        
        {/* Floating elements */}
        <motion.div
          variants={floatingVariants}
          animate="animate"
          style={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            zIndex: 0,
          }}
        >
          <AutoFixHigh sx={{ fontSize: 60, opacity: 0.1 }} />
        </motion.div>
        <motion.div
          variants={floatingVariants}
          animate="animate"
          style={{
            position: 'absolute',
            top: '60%',
            right: '15%',
            zIndex: 0,
            animationDelay: '1s',
          }}
        >
          <Psychology sx={{ fontSize: 80, opacity: 0.1 }} />
        </motion.div>
      </Box>

      {/* Mission Section */}
      <Box
        sx={{
          py: 16,
          backgroundColor: colors.light,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Chip
                  label="Our Mission"
                  sx={{
                    mb: 3,
                    backgroundColor: colors.primary,
                    color: '#fff',
                    fontWeight: 600,
                  }}
                />
                <Typography
                  variant="h2"
                  sx={{ fontWeight: 800, mb: 4, color: colors.dark }}
                >
                  Making Professional CV Creation 
                  <Box component="span" sx={{ color: colors.primary }}> Accessible to Everyone</Box>
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.8, fontWeight: 300 }}
                >
                  In today's competitive job market, your CV is often the first impression you make. 
                  We believe everyone deserves access to professional-quality CV creation tools.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.8 }}
                >
                  Many talented individuals struggle with presenting their experience effectively or 
                  optimizing their CVs for Applicant Tracking Systems (ATS). Our AI-powered platform 
                  bridges this gap by providing intelligent suggestions and professional formatting.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 6, color: 'text.secondary', lineHeight: 1.8 }}
                >
                  We're committed to democratizing access to quality career tools, helping job seekers 
                  at all levels present their best selves to potential employers.
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: colors.success }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ATS-Optimized Templates
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: colors.success }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      AI-Powered Suggestions
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: colors.success }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Modern Design Standards
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ x: 100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
                  }}
                >
                  <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1551836022-deb4988cc6c0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                    alt="Professional workspace"
                    sx={{ width: '100%', height: '400px', objectFit: 'cover' }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(45deg, ${colors.primary}aa, transparent)`,
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box
        sx={{
          py: 16,
          backgroundColor: '#fff',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <Typography
              variant="h2"
              textAlign="center"
              sx={{
                fontWeight: 800,
                mb: 3,
                color: colors.dark,
              }}
            >
              Why Choose 
              <Box component="span" sx={{ color: colors.primary }}>cleverCV</Box>
            </Typography>
            <Typography
              variant="h6"
              textAlign="center"
              sx={{
                mb: 12,
                maxWidth: '600px',
                mx: 'auto',
                color: 'text.secondary',
                fontWeight: 300,
              }}
            >
              Advanced features designed to give you an edge in today's competitive job market.
            </Typography>
            
            <Grid container spacing={6}>
              {values.map((value, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <motion.div variants={itemVariants}>
                    <Card
                      sx={{
                        height: '100%',
                        borderRadius: 6,
                        border: 'none',
                        background: value.gradient,
                        color: '#fff',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                          transform: 'translateY(-10px) scale(1.02)',
                          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 6, position: 'relative', zIndex: 1 }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 4,
                            backdropFilter: 'blur(10px)',
                          }}
                        >
                          {value.icon}
                        </Box>
                        <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
                          {value.title}
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.7, opacity: 0.9 }}>
                          {value.description}
                        </Typography>
                      </CardContent>
                      
                      {/* Decorative elements */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -50,
                          right: -50,
                          width: 100,
                          height: 100,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                        }}
                      />
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Technology Section */}
      <Box
        sx={{
          py: 16,
          backgroundColor: colors.dark,
          color: '#fff',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            sx={{ fontWeight: 800, mb: 3 }}
          >
            Powered by 
            <Box component="span" sx={{ color: colors.accent }}>Advanced Technology</Box>
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            sx={{
              mb: 10,
              maxWidth: '700px',
              mx: 'auto',
              opacity: 0.8,
              fontWeight: 300,
            }}
          >
            Our platform leverages modern AI and machine learning techniques to provide 
            intelligent CV optimization and formatting suggestions.
          </Typography>
          
          <Grid container spacing={4}>
            {techStack.map((tech, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <Paper
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 4,
                      transition: 'all 0.3s',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        borderColor: tech.color,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        color: tech.color,
                        mb: 2,
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      {tech.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
                      {tech.name}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box
        sx={{
          py: 16,
          backgroundColor: colors.light,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            sx={{
              fontWeight: 800,
              mb: 3,
              color: colors.dark,
            }}
          >
            How It 
            <Box component="span" sx={{ color: colors.primary }}>Works</Box>
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            sx={{
              mb: 12,
              maxWidth: '600px',
              mx: 'auto',
              color: 'text.secondary',
              fontWeight: 300,
            }}
          >
            Create your professional CV in just a few simple steps.
          </Typography>
          
          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <Card
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 4,
                    height: '100%',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: colors.primary,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                    }}
                  >
                    1
                  </Box>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                    Input Your Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enter your work experience, education, skills, and other relevant details 
                    through our intuitive interface.
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <Card
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 4,
                    height: '100%',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: colors.accent,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                    }}
                  >
                    2
                  </Box>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                    AI Optimization
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Our AI analyzes your content and provides suggestions for improvement, 
                    keyword optimization, and professional formatting.
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <Card
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 4,
                    height: '100%',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: colors.success,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                    }}
                  >
                    3
                  </Box>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                    Download & Apply
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Download your professionally formatted CV in multiple formats 
                    and start applying to your dream jobs with confidence.
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 20,
          background: `linear-gradient(135deg, ${colors.dark} 0%, ${colors.primary} 100%)`,
          color: '#fff',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated background */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            '&::before': {
              content: '""',
              position: 'absolute',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.05) 2px, transparent 2px)',
              backgroundSize: '100px 100px',
              animation: 'float 30s linear infinite reverse',
            },
          }}
        />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" sx={{ fontWeight: 900, mb: 4 }}>
              Ready to Create Your 
              <Box component="span" sx={{ color: colors.accent }}>Professional CV?</Box>
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 8,
                opacity: 0.9,
                fontWeight: 300,
                lineHeight: 1.4,
              }}
            >
              Join the thousands of job seekers who have enhanced their career prospects 
              with AI-powered CV creation. Start building your future today.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/generate"
                  sx={{
                    px: 8,
                    py: 4,
                    borderRadius: '50px',
                    backgroundColor: colors.accent,
                    fontSize: '1.3rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                    '&:hover': {
                      backgroundColor: colors.accent,
                      boxShadow: '0 15px 50px rgba(0,0,0,0.4)',
                      filter: 'brightness(110%)',
                    },
                  }}
                  endIcon={<ArrowForward />}
                >
                  Get Started Now
                </Button>
              </motion.div>
            </Box>
            
            <Typography
              variant="body2"
              sx={{
                mt: 4,
                opacity: 0.7,
              }}
            >
              ✨ Free to start • 🚀 Quick and easy • 🔒 Secure and private
            </Typography>
          </motion.div>
        </Container>
        
        {/* Floating elements */}
        <motion.div
          animate={{
            y: [0, -20, 0],
            rotate: [0, 5, 0],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          style={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            opacity: 0.1,
          }}
        >
          <Rocket sx={{ fontSize: 100 }} />
        </motion.div>
        <motion.div
          animate={{
            y: [0, -15, 0],
            rotate: [0, -5, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
          style={{
            position: 'absolute',
            bottom: '20%',
            right: '10%',
            opacity: 0.1,
          }}
        >
          <SmartToy sx={{ fontSize: 80 }} />
        </motion.div>
      </Box>

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes float {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0%); }
        }
      `}</style>
    </Box>
  );
};

export default AboutPage;